export const DEFAULT_TEST_ACCOUNT_IDS = ["apH2Vpa4qns0YTnelDMX", "UPyzRTUdKGlJgiCCxseP", "RxnT7D5WGjPddb9dSCp4"]

const toMillis = (timestamp) => {
	if (!timestamp || typeof timestamp.seconds !== "number") return null
	return timestamp.seconds * 1000
}

const buildMatchKey = (client, location, created) => `${client || ""}::${location || ""}::${created || ""}`

// Group size for a booking: prefer the snapshot of the originating enquiry's
// group_size, falling back to the booking's own golfers + non_golfers (used by
// DIRECT bookings that were never an enquiry).
export const getBookingGroupSize = (bookingData) => {
	if (!bookingData) return 0
	const fromEnquiry = Number(bookingData.enquiry?.group_size)
	if (fromEnquiry > 0) return fromEnquiry
	const golfers = Number(bookingData.golfers) || 0
	const nonGolfers = Number(bookingData.non_golfers) || 0
	return golfers + nonGolfers
}

const formatRate = (bookingsCount, enquiriesCount) => {
	if (enquiriesCount <= 0) return "N/A"
	return ((bookingsCount / enquiriesCount) * 100).toFixed(2)
}

export const getBookingReferenceSeconds = (bookingData) => {
	if (!bookingData) return null
	return bookingData.enquiry?.created?.seconds ?? bookingData.created?.seconds ?? null
}

export const buildCanonicalConversionDataset = ({
	openEnquiryDocs = [],
	removedEnquiryDocs = [],
	enquiryBookingDocs = [],
	directBookingDocs = [],
	allowedAgentIds = [],
	testAccountIds = DEFAULT_TEST_ACCOUNT_IDS,
}) => {
	const allowedAgentSet = new Set(allowedAgentIds)
	const hasAgentFilter = allowedAgentSet.size > 0
	const testAccountSet = new Set(testAccountIds)

	const includeAgent = (agentId) => !hasAgentFilter || allowedAgentSet.has(agentId)

	const openEnquiries = openEnquiryDocs
		.map((doc) => {
			const data = doc.data()
			const created = toMillis(data.created)

			if (data.archived_from_stats === true) return null
			if (!created || !data.client || !includeAgent(data.agent)) return null
			if (testAccountSet.has(data.client)) return null

			return {
				id: doc.id,
				created,
				agent: data.agent,
				client: data.client,
				location: data.location,
				site: data.site,
				group_size: Number(data.group_size) || 0,
			}
		})
		.filter(Boolean)

	const removedEnquiries = removedEnquiryDocs
		.map((doc) => {
			const data = doc.data()
			const enquiry = data.enquiry || {}
			const created = toMillis(enquiry.created)

			if (data.archived_from_stats === true) return null
			if (!created || !enquiry.client || !includeAgent(enquiry.agent)) return null
			if (testAccountSet.has(enquiry.client)) return null

			return {
				id: doc.id,
				created,
				agent: enquiry.agent,
				client: enquiry.client,
				location: enquiry.location,
				site: enquiry.site,
				group_size: Number(enquiry.group_size) || 0,
			}
		})
		.filter(Boolean)

	const enquiryBookings = enquiryBookingDocs
		.map((doc) => {
			const data = doc.data()
			const created = toMillis(data.enquiry?.created)

			if (!created || !data.client || !includeAgent(data.agent)) return null
			if (testAccountSet.has(data.client)) return null

			return {
				id: doc.id,
				created,
				client: data.client,
				agent: data.agent,
				location: data.enquiry?.location,
				site: data.enquiry?.site,
				margins: data.margins,
				group_size: getBookingGroupSize(data),
			}
		})
		.filter(Boolean)

	const directBookings = directBookingDocs
		.map((doc) => {
			const data = doc.data()
			const created = toMillis(data.created)

			if (data.booking_type !== "DIRECT") return null
			if (!created || !data.client || !includeAgent(data.agent)) return null
			if (testAccountSet.has(data.client)) return null

			return {
				id: doc.id,
				created,
				client: data.client,
				agent: data.agent,
				location: data.enquiry?.location,
				site: data.enquiry?.site,
				margins: data.margins,
				group_size: getBookingGroupSize(data),
			}
		})
		.filter(Boolean)

	const allEnquiries = [...openEnquiries, ...removedEnquiries]
	const allBookings = [...enquiryBookings, ...directBookings]

	const bookingMatchKeys = new Set(allBookings.map((booking) => buildMatchKey(booking.client, booking.location, booking.created)))
	const allEnquiriesWithBookedFlag = allEnquiries.map((enquiry) => ({
		...enquiry,
		booked: bookingMatchKeys.has(buildMatchKey(enquiry.client, enquiry.location, enquiry.created)),
	}))

	const bookedEnquiries = allEnquiriesWithBookedFlag.filter((enquiry) => enquiry.booked)
	const nonBookedEnquiries = allEnquiriesWithBookedFlag.filter((enquiry) => !enquiry.booked)

	const clientsWithAnyBooking = new Set(allBookings.map((booking) => booking.client))
	const filteredNonBookedEnquiries = nonBookedEnquiries.filter((enquiry) => !clientsWithAnyBooking.has(enquiry.client))
	const dedupedNonBookedEnquiries = filteredNonBookedEnquiries.filter(
		(enquiry, index, list) => list.findIndex((item) => item.client === enquiry.client) === index
	)

	const finalEnquiries = [...dedupedNonBookedEnquiries, ...bookedEnquiries]

	return {
		openEnquiries,
		removedEnquiries,
		allEnquiries,
		finalEnquiries,
		bookedEnquiries,
		enquiryBookings,
		directBookings,
		allBookings,
	}
}

export const buildNewRepeatBreakdown = ({ groupId, groupKey = "agent", enquiries = [], bookings = [], historyMap = new Map() }) => {
	const groupEnquiries = enquiries.filter((enquiry) => enquiry[groupKey] === groupId)
	const groupBookings = bookings.filter((booking) => booking[groupKey] === groupId)

	let newEnquiriesCount = 0
	let repeatEnquiriesCount = 0

	groupEnquiries.forEach((enquiry) => {
		const clientHistoryTimestamp = historyMap.get(enquiry.client)
		const enquiryTimestamp = Math.floor(enquiry.created / 1000)
		const isRepeat = clientHistoryTimestamp && clientHistoryTimestamp < enquiryTimestamp

		if (isRepeat) repeatEnquiriesCount++
		else newEnquiriesCount++
	})

	let newBookingsCount = 0
	let repeatBookingsCount = 0

	groupBookings.forEach((booking) => {
		const clientHistoryTimestamp = historyMap.get(booking.client)
		const bookingTimestamp = Math.floor(booking.created / 1000)
		const isRepeat = clientHistoryTimestamp && clientHistoryTimestamp < bookingTimestamp

		if (isRepeat) repeatBookingsCount++
		else newBookingsCount++
	})

	return {
		newConversion: formatRate(newBookingsCount, newEnquiriesCount),
		repeatConversion: formatRate(repeatBookingsCount, repeatEnquiriesCount),
		newEnquiriesCount,
		repeatEnquiriesCount,
		newBookingsCount,
		repeatBookingsCount,
	}
}
